import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import './LandingPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Demo data (hardware store items for the animated invoice) ──
const DEMO_ITEMS = [
  { name: 'Bosch Drill Machine', price: 2450 },
  { name: 'Steel Hammer 500g',   price: 320  },
  { name: 'PVC Pipe 2 inch',     price: 180  },
  { name: 'LED Bulb 9W (Pack of 4)', price: 240 },
];

const TAGS = [
  'Real-time Stock Alerts', 'GST Billing', 'WhatsApp Invoice', 'Staff Attendance',
  'Customer Ledger', 'UPI Payments', 'Offline Billing', 'Sales Reports',
];

const FEATURES = [
  { icon: '📦', title: 'Inventory Tracking', desc: 'Get an automatic alert the moment stock runs low. Never run out of a product without knowing.' },
  { icon: '🧾', title: 'GST Billing', desc: 'Generate a GST-ready invoice in one click — no accountant needed. Send it straight over WhatsApp or email.' },
  { icon: '👥', title: 'Customer Ledger', desc: 'Every customer\'s dues and payment history, all in one place. Say goodbye to the old paper ledger.' },
  { icon: '👨‍💼', title: 'Staff & Attendance', desc: 'Manage your staff\'s attendance, salary, and roles — all from a single dashboard.' },
  { icon: '💰', title: 'Payment Tracking', desc: 'See who paid and who didn\'t, in real time. Payment reminders go out automatically.' },
  { icon: '📊', title: 'Reports & Insights', desc: 'See how your business is doing daily, weekly, or monthly — clearly laid out in graphs and numbers.' },
];

const INTEGRATIONS = [
  { icon: '💬', title: 'WhatsApp', desc: 'Send invoices, payment reminders, and stock alerts straight to your customer\'s WhatsApp.' },
  { icon: '📲', title: 'UPI Payments', desc: 'Collect payments instantly with a QR code — GPay, PhonePe, Paytm, all supported.' },
  { icon: '✉️', title: 'Email', desc: 'Send a professional invoice PDF to your customer\'s email in one click, too.' },
];

const PLANS = [
  {
    name: 'Starter', price: '499', period: '/month',
    desc: 'For small shops that are just getting started.',
    features: ['1 User Login', 'Up to 100 Products', 'GST Billing', 'WhatsApp Invoice'],
    highlight: false,
  },
  {
    name: 'Growth', price: '999', period: '/month',
    desc: 'For growing shops that need staff access and more control.',
    features: ['5 Staff Logins', 'Unlimited Products', 'Advanced Reports', 'Attendance Tracking', 'Priority Support'],
    highlight: true,
  },
  {
    name: 'Enterprise', price: 'Custom', period: '',
    desc: 'For multi-branch or large businesses.',
    features: ['Unlimited Staff', 'Multi-Branch Support', 'Dedicated Manager', 'Onboarding Training'],
    highlight: false,
  },
];

const FAQS = [
  { q: 'Are the invoices valid under GST rules?', a: 'Yes, absolutely. Every invoice generated by Bhamsa follows GST rules — CGST and SGST are calculated automatically.' },
  { q: 'Is my data safe?', a: 'Yes. Your data is stored on encrypted cloud servers and backed up daily. Even if your phone is lost or crashes, your data stays safe.' },
  { q: 'Can I give my staff their own login?', a: 'Yes, the Growth and Enterprise plans include multiple staff logins, so you can track their activity too.' },
  { q: 'Is there a free trial?', a: 'Yes, you get a 7-day free trial — no card details required.' },
  { q: 'Can I bill without an internet connection?', a: 'Yes, our "Offline Sale" feature lets you bill even without internet — it syncs automatically once you\'re back online.' },
];

// ── Signature "live invoice" mockup — reused as a motif across the page ──
const InvoiceGraphic = () => {
  const [step, setStep] = useState(0);
  const total = DEMO_ITEMS.length + 2;
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % (total + 3)), 950);
    return () => clearInterval(t);
  }, [total]);
  const items = DEMO_ITEMS.slice(0, Math.min(step, DEMO_ITEMS.length));
  const subtotal = items.reduce((s, i) => s + i.price, 0);
  const showSub = step > DEMO_ITEMS.length - 1;
  const showTotal = step > DEMO_ITEMS.length;
  const gst = showSub ? +(subtotal * 0.18).toFixed(2) : 0;
  const grand = showSub ? +(subtotal + gst).toFixed(2) : 0;

  return (
    <div className="mock-card mock-invoice">
      <div className="mock-invoice-head">
        <span>⚡ Bhamsa</span>
        <span className="mock-chip">INVOICE</span>
      </div>
      <div className="mock-invoice-items">
        {items.map(i => (
          <div className="mock-row mock-row-in" key={i.name}><span>{i.name}</span><span>₹{i.price}</span></div>
        ))}
      </div>
      {showSub && (
        <>
          <div className="mock-divider" />
          <div className="mock-row mock-dim"><span>Subtotal</span><span>₹{subtotal}</span></div>
          <div className="mock-row mock-dim"><span>GST (18%)</span><span>₹{gst}</span></div>
        </>
      )}
      {showTotal && <div className="mock-row mock-total"><span>Total</span><span>₹{grand}</span></div>}
    </div>
  );
};

// ── Small mockup graphics for alternating feature sections ──
const StockGraphic = () => (
  <div className="mock-card mock-stock">
    <div className="mock-stock-head">Inventory</div>
    {[
      { name: 'Cordless Screwdriver Set', pct: 78, ok: true },
      { name: 'Copper Wire 90m Coil', pct: 12, ok: false },
      { name: 'Anchor Fan 1200mm', pct: 55, ok: true },
    ].map(r => (
      <div className="mock-stock-row" key={r.name}>
        <span>{r.name}</span>
        <div className="mock-stock-bar"><div className="mock-stock-fill" style={{ width: r.pct + '%', background: r.ok ? 'var(--lp-accent2)' : 'var(--lp-danger)' }} /></div>
        {!r.ok && <span className="mock-stock-warn">Low Stock</span>}
      </div>
    ))}
  </div>
);

const DashGraphic = () => (
  <div className="mock-card mock-dash">
    <div className="mock-dash-grid">
      <div className="mock-dash-tile"><div className="mock-dash-num">128</div><div className="mock-dash-label">Customers</div></div>
      <div className="mock-dash-tile"><div className="mock-dash-num">₹42k</div><div className="mock-dash-label">Today's Sale</div></div>
      <div className="mock-dash-tile"><div className="mock-dash-num">6</div><div className="mock-dash-label">Staff Active</div></div>
      <div className="mock-dash-tile"><div className="mock-dash-num">312</div><div className="mock-dash-label">Products</div></div>
    </div>
    <div className="mock-dash-bars">
      {[40, 65, 30, 80, 55, 90, 45].map((h, i) => (
        <div key={i} className="mock-dash-bar" style={{ height: h + '%' }} />
      ))}
    </div>
  </div>
);

// ── Main Page ───────────────────────────────────────────────
const LandingPage = () => {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/contact`, form);
      toast.success('Thank you! We will get back to you soon. 🙏');
      setForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong, please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lp-root">
      {/* Navbar */}
      <header className={"lp-nav" + (scrolled ? ' lp-nav-scrolled' : '')}>
        <div className="lp-nav-inner">
          <div className="lp-brand">⚡ Bhamsa</div>
          <nav className={"lp-nav-links" + (navOpen ? ' lp-nav-links-open' : '')}>
            <button onClick={() => scrollTo('features')}>Features</button>
            <button onClick={() => scrollTo('integrations')}>Integrations</button>
            <button onClick={() => scrollTo('pricing')}>Pricing</button>
            <button onClick={() => scrollTo('faq')}>FAQ</button>
            <Link to="/login" className="lp-nav-login-mobile">Login</Link>
          </nav>
          <div className="lp-nav-right">
          <Link to="/login" className="lp-btn lp-btn-primary lp-btn-sm">Login</Link>
            <button className="lp-hamburger" onClick={() => setNavOpen(!navOpen)} aria-label="Menu">
              {navOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="lp-hero">
        <div className="lp-eyebrow">🇮🇳 Built for Indian retailers</div>
        <h1 className="lp-h1">
          Bill Faster<br />
          <span className="lp-h1-accent">Run Your Shop Smarter</span>
        </h1>
        <p className="lp-hero-sub">
          Inventory, GST billing, staff, and customers — all from one dashboard. Say goodbye to the register and calculator.
        </p>
        <div className="lp-hero-cta">
          <button onClick={() => scrollTo('contact')} className="lp-btn lp-btn-primary">Trial One Month</button>
          <button className="lp-btn lp-btn-outline" onClick={() => scrollTo('features')}>See Features</button>
        </div>
        <div className="lp-hero-graphic">
          <InvoiceGraphic />
        </div>
      </section>

      {/* Tag marquee */}
      <div className="lp-marquee">
        <div className="lp-marquee-track">
          {[...TAGS, ...TAGS].map((t, i) => <span className="lp-tag" key={i}>{t}</span>)}
        </div>
      </div>

      {/* Alternating feature sections */}
      <section className="lp-feature-row" id="features">
        <div className="lp-feature-text">
          <div className="lp-section-eyebrow">Real-Time Monitoring</div>
          <h2 className="lp-h2">Stock never runs out without you knowing</h2>
          <p className="lp-p">See a live stock level for every product. The moment something starts running low, you get an instant alert — you'll never have to tell a customer "we're out" again.</p>
        </div>
        <div className="lp-feature-graphic"><StockGraphic /></div>
      </section>

      <section className="lp-feature-row lp-feature-row-rev">
        <div className="lp-feature-text">
          <div className="lp-section-eyebrow">GST Billing</div>
          <h2 className="lp-h2">Invoices build themselves, in seconds</h2>
          <p className="lp-p">Select an item and GST plus the total calculate automatically. Print the bill or send it straight to WhatsApp and email — no accountant required.</p>
        </div>
        <div className="lp-feature-graphic"><InvoiceGraphic /></div>
      </section>

      <section className="lp-feature-row">
        <div className="lp-feature-text">
          <div className="lp-section-eyebrow">One Dashboard</div>
          <h2 className="lp-h2">Your entire shop, tracked from one place</h2>
          <p className="lp-p">Customers, staff, and sales — all on one dashboard. See exactly how your business did today at a glance.</p>
        </div>
        <div className="lp-feature-graphic"><DashGraphic /></div>
      </section>

      {/* Feature grid */}
      <section className="lp-section lp-section-alt">
        <div className="lp-section-head">
          <div className="lp-section-eyebrow">Everything in One Place</div>
          <h2 className="lp-h2">Everything you need to run your shop</h2>
        </div>
        <div className="lp-grid lp-grid-3">
          {FEATURES.map(f => (
            <div className="lp-card" key={f.title}>
              <div className="lp-card-icon">{f.icon}</div>
              <div className="lp-card-title">{f.title}</div>
              <div className="lp-card-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Integrations */}
      <section className="lp-section" id="integrations">
        <div className="lp-section-head">
          <div className="lp-section-eyebrow">Integrations</div>
          <h2 className="lp-h2">Works with the tools you already use</h2>
        </div>
        <div className="lp-grid lp-grid-3">
          {INTEGRATIONS.map(i => (
            <div className="lp-card lp-integration-card" key={i.title}>
              <div className="lp-card-icon">{i.icon}</div>
              <div className="lp-card-title">{i.title}</div>
              <div className="lp-card-desc">{i.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="lp-section lp-section-alt" id="pricing">
        <div className="lp-section-head">
          <div className="lp-section-eyebrow">Pricing</div>
          <h2 className="lp-h2">Simple, straightforward pricing</h2>
          <p className="lp-p lp-center">No hidden charges. Cancel anytime.</p>
        </div>
        <div className="lp-grid lp-grid-3">
          {PLANS.map(p => (
            <div className={"lp-plan" + (p.highlight ? ' lp-plan-highlight' : '')} key={p.name}>
              {p.highlight && <div className="lp-plan-badge">Most Popular</div>}
              <div className="lp-plan-name">{p.name}</div>
              <div className="lp-plan-price">
                {p.price !== 'Custom' && <span>₹</span>}{p.price}
                {p.period && <span className="lp-plan-period">{p.period}</span>}
              </div>
              <div className="lp-p">{p.desc}</div>
              <ul className="lp-plan-list">
                {p.features.map(f => <li key={f}>✓ {f}</li>)}
              </ul>
              <button onClick={() => scrollTo('contact')} className={"lp-btn " + (p.highlight ? 'lp-btn-primary' : 'lp-btn-outline') + ' lp-plan-btn'}>
                Get Started
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA banner */}
      <section className="lp-cta-banner">
        <h2>Your Next Successful Sale<br />Is Just One Click Away</h2>
        <p>7-day free trial, no card required.</p>
        <button onClick={() => scrollTo('contact')} className="lp-btn lp-btn-white">Start Free Trial</button>
      </section>

      {/* FAQ */}
      <section className="lp-section" id="faq">
        <div className="lp-section-head">
          <div className="lp-section-eyebrow">FAQ</div>
          <h2 className="lp-h2">Answers to Your Questions</h2>
        </div>
        <div className="lp-faq-list">
          {FAQS.map((f, i) => (
            <div className={"lp-faq-item" + (openFaq === i ? ' lp-faq-open' : '')} key={f.q}>
              <button className="lp-faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                <span>{f.q}</span>
                <span className="lp-faq-arrow">{openFaq === i ? '−' : '+'}</span>
              </button>
              {openFaq === i && <div className="lp-faq-a">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="lp-section lp-section-alt" id="contact">
        <div className="lp-section-head">
          <div className="lp-section-eyebrow">Contact</div>
          <h2 className="lp-h2">Let's Get in Touch</h2>
          <p className="lp-p lp-center">Want a demo or have a question? Fill out the form and our team will reach out within 24 hours.</p>
        </div>
        <form className="lp-form" onSubmit={submit}>
          <div className="lp-form-row">
            <input className="lp-input" placeholder="Your Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input className="lp-input" placeholder="Email Address" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <input className="lp-input" placeholder="Shop / Company (optional)" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} />
          <textarea className="lp-input" rows={4} placeholder="Message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
          <button className="lp-btn lp-btn-primary" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-tag">Built for clarity.<br />Designed for results.</div>
        <div className="lp-footer-links">
          <button onClick={() => scrollTo('features')}>Features</button>
          <button onClick={() => scrollTo('pricing')}>Pricing</button>
          <button onClick={() => scrollTo('faq')}>FAQ</button>
          <Link to="/login">Login</Link>
        </div>
        <div className="lp-footer-bottom">© {new Date().getFullYear()} Bhamsa. All rights reserved.</div>
      </footer>
    </div>
  );
};

export default LandingPage;